import { useNuxtApp } from '#app'
import { ToastAction } from '#components'
import type { Id, ToastContent, Content, ToastOptions, ToastType } from 'vue3-toastify'

export interface StatefulToastContent {
  message: string
  details?: string
  action?: {
    label: string
    click: () => Promise<void> | void
  }
}

export type StatefulToastOptions = Omit<
  ToastOptions<StatefulToastContent>,
  'autoClose' | 'closeButton' | 'closeOnClick' | 'data' | 'isLoading' | 'toastId' | 'type'
>

export type StatefulToastInput = string | StatefulToastContent
type SettledToastType = Extract<ToastType, 'error' | 'success'>

const normalizeContent = (content: StatefulToastInput): StatefulToastContent =>
  typeof content === 'string' ? { message: content } : content

export const generateToastId = (): Id => globalThis.crypto?.randomUUID?.() ?? Math.random()

export const useStatefulToast = (providedToastId?: Id) => {
  const { $toast } = useNuxtApp()
  const toastId = providedToastId ?? generateToastId()
  let hasPendingToast = false
  let hasToast = false

  const pending = (input: StatefulToastInput, options: StatefulToastOptions = {}) => {
    const content = normalizeContent(input)

    if (hasPendingToast || $toast.isActive(toastId)) {
      $toast.update(toastId, {
        ...options,
        toastId,
        render: ToastAction as ToastContent<unknown>,
        data: content,
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        type: 'loading',
        isLoading: true,
      })
    } else {
      $toast.loading(ToastAction as Content, {
        ...options,
        toastId,
        data: content,
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        type: 'loading',
      })
    }

    hasPendingToast = true
    hasToast = true
  }

  const settle = (
    type: SettledToastType,
    input: StatefulToastInput,
    options: StatefulToastOptions = {},
  ) => {
    const content = normalizeContent(input)
    const settledOptions = {
      ...options,
      data: content,
      autoClose: content.action ? false : type === 'success' ? 5000 : 2000,
      closeOnClick: !content.action,
      closeButton: false,
    }

    if (!hasPendingToast && !$toast.isActive(toastId)) {
      if (type === 'success') {
        $toast.success(ToastAction as Content, {
          ...settledOptions,
          toastId,
        })
      } else {
        $toast.error(ToastAction as Content, {
          ...settledOptions,
          toastId,
        })
      }
    } else {
      $toast.update(toastId, {
        ...settledOptions,
        toastId,
        render: ToastAction as ToastContent<unknown>,
        type,
        isLoading: false,
      })
    }

    hasPendingToast = false
    hasToast = true
  }

  const success = (input: StatefulToastInput, options?: StatefulToastOptions) =>
    settle('success', input, options)

  const error = (input: StatefulToastInput, options?: StatefulToastOptions) =>
    settle('error', input, options)

  const dismiss = () => {
    if (!hasToast && !$toast.isActive(toastId)) return

    $toast.remove(toastId)
    hasPendingToast = false
    hasToast = false
  }

  return {
    toastId,
    pending,
    success,
    error,
    dismiss,
  }
}
